import { apiSlice } from "../../../features/api/apiSlice";

/**
 * Logbook Assessment Diets — a separate assessment cycle for already-submitted
 * logbooks. Backend routes (prefix /logbook-assessment-diets):
 *   GET    /                                   — list diets (with counts)
 *   POST   /                                   — create diet
 *   GET    /assessors                          — assessor pool (by member id)
 *   GET    /awaiting-logbooks                   — logbooks awaiting assessment
 *   GET    /{id}                               — diet detail (assessors + logbooks)
 *   PUT    /{id}                               — update diet
 *   DELETE /{id}                               — delete diet
 *   POST   /{id}/activate | /{id}/close        — lifecycle
 *   PUT    /{id}/assessors                     — sync assessors { assessor_member_ids }
 *   DELETE /{id}/assessors/{memberId}          — remove one assessor
 *   POST   /{id}/logbooks/assign               — { assessor_member_id, logbook_ids }
 *   GET    /{id}/assessors/{memberId}/logbooks — an assessor's assigned logbooks
 *   POST   /{id}/logbooks/{logbookId}/unassign — take a logbook back out
 *   POST   /{id}/manual-result                 — type in a paper/off-system result
 */

export type AssessmentDiet = {
  id: string;
  title: string;
  start_date: string | null;
  end_date: string | null;
  status: string; // pending | ongoing | closed
  is_active: number | boolean;
  /**
   * This sitting ran under the SINGLE-LOGBOOK scheme: one logbook stood for all
   * three stages, so a pass in it completes the practical requirement outright
   * rather than clearing one stage of three.
   *
   * Undefined on a server whose migration has not run, where every diet counts
   * as one stage — which is how things behaved before this existed.
   */
  covers_all_stages?: boolean;
  notes?: string | null;
  assessors_count?: number;
  logbooks_count?: number;
  created_at?: string;
  updated_at?: string;
};

export type AssessorPoolItem = {
  member_id: string;
  name: string;
  email?: string;
};

export type AssessmentDietAssessorStat = {
  assessor_id: string | null; // assessors.id — for linking to /assessors/:id
  member_id: string;
  name: string;
  email?: string;
  total_assigned_logbooks: number;
  total_assessed_logbooks: number;
  total_pending_logbooks: number;
};

export type AssessmentDietDetails = {
  diet: AssessmentDiet & { assessors?: any[] };
  summary: {
    total_logbooks: number;
    assessed: number;
    pending: number;
    total_assessors: number;
  };
  assessors: AssessmentDietAssessorStat[];
  logbooks: any[];
};

export type MyAssessmentDiet = {
  id: string;
  title: string;
  status: string; // pending | ongoing | closed
  is_active: number | boolean;
  start_date: string | null;
  end_date: string | null;
  notes?: string | null;
  my_total_logbooks: number;
  my_assessed_logbooks: number;
  my_pending_logbooks: number;
};

export type MyAssessmentDietLogbooks = {
  diet: AssessmentDiet;
  summary: { total: number; assessed: number; pending: number };
  data: any[];
};

type CreateDietBody = {
  title: string;
  start_date?: string | null;
  end_date?: string | null;
  notes?: string | null;
  /** See AssessmentDiet.covers_all_stages. */
  covers_all_stages?: boolean;
};

export const logbookAssessmentDietApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // List all assessment diets (with assessor/logbook counts).
    getAssessmentDiets: builder.query<AssessmentDiet[], void>({
      query: () => "/logbook-assessment-diets",
      transformResponse: (res: any) => res?.data ?? res ?? [],
      providesTags: [{ type: "LbAssessDiets" as const, id: "LIST" }],
    }),

    // One diet with its assessors (workload) + assigned logbooks + summary.
    getAssessmentDietDetails: builder.query<AssessmentDietDetails, string>({
      query: (id) => `/logbook-assessment-diets/${id}`,
      providesTags: (_r, _e, id) => [{ type: "LbAssessDietDetails" as const, id }],
    }),

    createAssessmentDiet: builder.mutation<any, CreateDietBody>({
      query: (body) => ({ url: "/logbook-assessment-diets", method: "POST", body }),
      invalidatesTags: [{ type: "LbAssessDiets" as const, id: "LIST" }],
    }),

    updateAssessmentDiet: builder.mutation<any, { id: string; body: Partial<CreateDietBody> }>({
      query: ({ id, body }) => ({ url: `/logbook-assessment-diets/${id}`, method: "PUT", body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "LbAssessDietDetails" as const, id },
        { type: "LbAssessDiets" as const, id: "LIST" },
      ],
    }),

    deleteAssessmentDiet: builder.mutation<any, string>({
      query: (id) => ({ url: `/logbook-assessment-diets/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "LbAssessDiets" as const, id: "LIST" }],
    }),

    // Lifecycle — POST (the host blocks PATCH).
    activateAssessmentDiet: builder.mutation<any, string>({
      query: (id) => ({ url: `/logbook-assessment-diets/${id}/activate`, method: "POST" }),
      invalidatesTags: (_r, _e, id) => [
        { type: "LbAssessDietDetails" as const, id },
        { type: "LbAssessDiets" as const, id: "LIST" },
      ],
    }),

    closeAssessmentDiet: builder.mutation<any, string>({
      query: (id) => ({ url: `/logbook-assessment-diets/${id}/close`, method: "POST" }),
      invalidatesTags: (_r, _e, id) => [
        { type: "LbAssessDietDetails" as const, id },
        { type: "LbAssessDiets" as const, id: "LIST" },
      ],
    }),

    /**
     * Apply the diet's cut-off mark. Grades every assessed logbook pass or fail
     * and sets the re-sit period — six months for falling below the mark,
     * twelve for contravening. Send dry_run to preview the split without
     * writing; the response carries the same `summary` either way.
     *
     * Does not release anything: the board's approve step still controls what a
     * member sees, which is what makes this safe to re-run.
     */
    applyAssessmentDietCutOff: builder.mutation<
      any,
      { id: string; cut_off: number; dry_run?: boolean }
    >({
      query: ({ id, ...body }) => ({
        url: `/logbook-assessment-diets/${id}/apply-cut-off`,
        method: "POST",
        body,
      }),
      // A preview writes nothing, so it must not invalidate the screen it is
      // previewing — refetching would throw away the numbers just shown.
      invalidatesTags: (_r, _e, { id, dry_run }) =>
        dry_run ? [] : [{ type: "LbAssessDietDetails" as const, id }],
    }),

    /**
     * Per-diet wording for the candidate's result letter — one block per
     * outcome, plus the signatory. The GET returns the diet's settings already
     * merged over the defaults, so the form is never blank.
     */
    getAssessmentDietResultLetter: builder.query<any, string>({
      query: (id) => `/logbook-assessment-diets/${id}/result-letter`,
      providesTags: (_r, _e, id) => [{ type: "LbResultLetter" as const, id }],
    }),

    saveAssessmentDietResultLetter: builder.mutation<any, { id: string; body: any }>({
      query: ({ id, body }) => ({
        url: `/logbook-assessment-diets/${id}/result-letter`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "LbResultLetter" as const, id }],
    }),

    // Section 1 — assessors.
    getAssessmentAssessorsPool: builder.query<AssessorPoolItem[], void>({
      query: () => "/logbook-assessment-diets/assessors",
      transformResponse: (res: any) => res?.data ?? res ?? [],
    }),

    syncAssessmentDietAssessors: builder.mutation<any, { id: string; assessor_member_ids: string[] }>({
      query: ({ id, assessor_member_ids }) => ({
        url: `/logbook-assessment-diets/${id}/assessors`,
        method: "PUT",
        body: { assessor_member_ids },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "LbAssessDietDetails" as const, id }],
    }),

    removeAssessmentDietAssessor: builder.mutation<any, { id: string; memberId: string }>({
      query: ({ id, memberId }) => ({
        url: `/logbook-assessment-diets/${id}/assessors/${encodeURIComponent(memberId)}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "LbAssessDietDetails" as const, id }],
    }),

    // Section 2 — logbook assignment.
    getAssessmentAwaitingLogbooks: builder.query<any[], void>({
      query: () => "/logbook-assessment-diets/awaiting-logbooks",
      transformResponse: (res: any) => res?.data ?? res ?? [],
      providesTags: [{ type: "LbAssessAwaiting" as const, id: "LIST" }],
    }),

    assignAssessmentLogbooks: builder.mutation<
      any,
      { id: string; assessor_member_id: string; logbook_ids: string[] }
    >({
      query: ({ id, ...body }) => ({
        url: `/logbook-assessment-diets/${id}/logbooks/assign`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "LbAssessDietDetails" as const, id },
        { type: "LbAssessAwaiting" as const, id: "LIST" },
      ],
    }),

    getAssessmentDietAssessorLogbooks: builder.query<any, { id: string; memberId: string }>({
      query: ({ id, memberId }) =>
        `/logbook-assessment-diets/${id}/assessors/${encodeURIComponent(memberId)}/logbooks`,
      transformResponse: (res: any) => res ?? {},
    }),

    unassignAssessmentLogbook: builder.mutation<any, { id: string; logbookId: string }>({
      query: ({ id, logbookId }) => ({
        url: `/logbook-assessment-diets/${id}/logbooks/${encodeURIComponent(logbookId)}/unassign`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "LbAssessDietDetails" as const, id },
        { type: "LbAssessAwaiting" as const, id: "LIST" },
      ],
    }),

    /* ---- Assessor-facing (the logged-in assessor's own assignments) ---- */

    // Diets I'm an assessor on, each with MY logbook workload.
    getMyAssessmentDiets: builder.query<MyAssessmentDiet[], void>({
      query: () => "/logbook-assessment-diets/my/diets",
      transformResponse: (res: any) =>
        Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [],
      providesTags: [{ type: "LbMyAssessDiets" as const, id: "LIST" }],
    }),

    // MY logbooks within a diet.
    getMyAssessmentDietLogbooks: builder.query<MyAssessmentDietLogbooks, string>({
      query: (id) => `/logbook-assessment-diets/${id}/my/logbooks`,
      transformResponse: (res: any) => res ?? {},
      providesTags: (_r, _e, id) => [{ type: "LbMyAssessDietLogbooks" as const, id }],
    }),

    /* ---- Board — result approval (release results to members) ---- */

    // Diet-assessed logbooks for the board's approval screen (pending|approved).
    getLogbookResultsForApproval: builder.query<any, "pending" | "approved" | void>({
      query: (status) =>
        `/logbook-assessment-diets/results/pending${
          status === "approved" ? "?status=approved" : ""
        }`,
      transformResponse: (res: any) => res ?? {},
      providesTags: [{ type: "LbResultApproval" as const, id: "LIST" }],
    }),

    // Approve (release) one or more assessed results.
    approveLogbookResults: builder.mutation<any, { logbook_ids: string[] }>({
      query: (body) => ({
        url: "/logbook-assessment-diets/results/approve",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "LbResultApproval" as const, id: "LIST" }],
    }),

    /**
     * Type in a result the Institute already has on paper — a candidate
     * assessed before logbook assessment went digital, or one otherwise
     * settled off the pipeline. No assessor, no grading criteria: the admin
     * records the outcome outright, and the record is stamped `is_manual` so
     * it is never mistaken for a digitally assessed one.
     */
    recordManualAssessmentResult: builder.mutation<
      any,
      {
        id: string;
        user_id: string;
        stage: number;
        result: "pass" | "resit_6_months" | "resit_12_months";
        total_score?: number;
        comment?: string;
        manual_source?: string;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/logbook-assessment-diets/${id}/manual-result`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "LbAssessDietDetails" as const, id },
        { type: "LbResultApproval" as const, id: "LIST" },
      ],
    }),

    // Grade one of MY assigned logbooks within the diet.
    assessAssessmentLogbook: builder.mutation<
      any,
      {
        id: string;
        logbookId: string;
        quality_of_entries: number;
        competency_coverage: number;
        originality_reality: number;
        report_writing: number;
        comment?: string;
        result: "pass" | "fail";
        repeat_type?: "full" | "half";
      }
    >({
      query: ({ id, logbookId, ...body }) => ({
        url: `/logbook-assessment-diets/${id}/logbooks/${encodeURIComponent(logbookId)}/assess`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "LbMyAssessDietLogbooks" as const, id },
        { type: "LbMyAssessDiets" as const, id: "LIST" },
        { type: "LbAssessDietDetails" as const, id },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAssessmentDietsQuery,
  useGetAssessmentDietDetailsQuery,
  useCreateAssessmentDietMutation,
  useUpdateAssessmentDietMutation,
  useDeleteAssessmentDietMutation,
  useActivateAssessmentDietMutation,
  useCloseAssessmentDietMutation,
  useApplyAssessmentDietCutOffMutation,
  useGetAssessmentDietResultLetterQuery,
  useSaveAssessmentDietResultLetterMutation,
  useGetAssessmentAssessorsPoolQuery,
  useSyncAssessmentDietAssessorsMutation,
  useRemoveAssessmentDietAssessorMutation,
  useGetAssessmentAwaitingLogbooksQuery,
  useAssignAssessmentLogbooksMutation,
  useGetAssessmentDietAssessorLogbooksQuery,
  useUnassignAssessmentLogbookMutation,
  useGetMyAssessmentDietsQuery,
  useGetMyAssessmentDietLogbooksQuery,
  useAssessAssessmentLogbookMutation,
  useGetLogbookResultsForApprovalQuery,
  useApproveLogbookResultsMutation,
  useRecordManualAssessmentResultMutation,
} = logbookAssessmentDietApi;
